import { get, isArray, uniq } from "lodash";
import { buildPath } from "./common";
import {
  IAction,
  IBase,
  IInput,
  ICalculate,
  ILabel,
  IImage,
  IName,
  IPieChart,
  IRawPlotlyChart,
  ISelect,
  ITemplate,
  ITimeChart,
  IWaterfallChart,
  IBarChart,
  I3dSurface,
  IMetadata,
  IList,
  ILocationMatrix,
  IDependencyIndex,
  I2dFigure,
  I2dStructure,
  IWaterfallFigure,
  IWaterfallStructure,
  I3dFigure,
  I3dStructure,
} from "./types";

const inputTypes = new Set(["number", "text", "select", "checkbox", "file", "radio", "range", "location", "location_matrix", "calculate"]);
const layoutTypes = new Set(["explorer", "tab", "group", "tabs", "anchors"]);

type ScopePath = (name: IName) => IName;

const identityPath: ScopePath = (name) => name;

type QueryBackedInput = ISelect | ICalculate;
type ActionConfigurableTemplate = IBase & {
  disable?: unknown;
  validate?: unknown;
};

const hasQuery = (input: IInput): input is QueryBackedInput =>
  input.type === "select" || input.type === "calculate";

const is2dStructureFigure = (figure: I2dFigure | I2dStructure): figure is I2dStructure =>
  (figure as I2dStructure).type === "structure";

const isWaterfallStructureFigure = (
  figure: IWaterfallFigure | IWaterfallStructure
): figure is IWaterfallStructure => (figure as IWaterfallStructure).type === "structure";

const is3dStructureFigure = (figure: I3dFigure | I3dStructure): figure is I3dStructure =>
  (figure as I3dStructure).type === "structure";

const collectActionSources = (value: unknown, scopePath: ScopePath, output: Set<IName>) => {
  if (!value || typeof value !== "object") {
    return;
  }

  if (isArray(value)) {
    value.forEach((item) => collectActionSources(item, scopePath, output));
    return;
  }

  const action = value as IAction;
  if (isArray(action.sources)) {
    action.sources.forEach((source) => output.add(scopePath(source)));
  }

  Object.values(value).forEach((item) => collectActionSources(item, scopePath, output));
};

const collectQuerySources = (input: IInput, scopePath: ScopePath, output: Set<IName>) => {
  const query = hasQuery(input) ? input.query : undefined;
  if (!query) {
    return;
  }

  collectActionSources(query.url, scopePath, output);
  collectActionSources(query.body, scopePath, output);
  collectActionSources(query.transform, scopePath, output);
};

const collectFieldDependencies = (input: IInput, scopePath: ScopePath): Array<IName> => {
  const dependencies = new Set<IName>();
  collectActionSources(input.default, scopePath, dependencies);
  collectQuerySources(input, scopePath, dependencies);
  if (input.type === "location_matrix") {
    collectActionSources((input as ILocationMatrix).locations, scopePath, dependencies);
  }
  return [...dependencies];
};

const collectTemplateDependencies = (template: IBase, scopePath: ScopePath): Array<IName> => {
  const dependencies = new Set<IName>();
  const configurableTemplate = template as ActionConfigurableTemplate;

  collectActionSources(template.hidden, scopePath, dependencies);
  collectActionSources(configurableTemplate.disable, scopePath, dependencies);
  collectActionSources(configurableTemplate.validate, scopePath, dependencies);

  switch (template.type) {
    case "label": {
      const label = template as ILabel;
      collectActionSources(label.format, scopePath, dependencies);
      if (!label.format?.sources?.length && label.name) {
        dependencies.add(scopePath(label.name));
      }
      collectActionSources(label.verify, scopePath, dependencies);
      collectActionSources(label.validate, scopePath, dependencies);
      break;
    }
    case "image": {
      const image = template as IImage;
      collectActionSources(image.url, scopePath, dependencies);
      break;
    }
    case "bar_chart": {
      const chart = template as IBarChart;
      chart.figures.forEach((figure) => {
        if (is2dStructureFigure(figure)) {
          dependencies.add(scopePath(figure.name));
        } else {
          dependencies.add(scopePath(figure.x_data.name));
          dependencies.add(scopePath(figure.y_data.name));
        }
      });
      chart.range?.forEach((name) => dependencies.add(scopePath(name)));
      break;
    }
    case "waterfall_chart": {
      const chart = template as IWaterfallChart;
      chart.figures.forEach((figure) => {
        if (isWaterfallStructureFigure(figure)) {
          dependencies.add(scopePath(figure.name));
        } else {
          dependencies.add(scopePath(figure.x_data.name));
          dependencies.add(scopePath(figure.y_data.name));
          if (figure.m_data?.name) {
            dependencies.add(scopePath(figure.m_data.name));
          }
        }
      });
      chart.range?.forEach((name) => dependencies.add(scopePath(name)));
      break;
    }
    case "pie_chart": {
      const chart = template as IPieChart;
      chart.figures.forEach((figure) => {
        if (isArray(figure.data)) {
          figure.data.forEach((datum) => dependencies.add(scopePath(datum.name)));
        } else if (figure.data?.name) {
          dependencies.add(scopePath(figure.data.name));
        }
      });
      break;
    }
    case "time_chart": {
      const chart = template as ITimeChart;
      chart.figures.forEach((figure) => {
        if (figure.startname) {
          dependencies.add(scopePath(figure.startname));
        }
        figure.data.forEach((datum) => dependencies.add(scopePath(datum.name)));
      });
      chart.range?.forEach((name) => dependencies.add(scopePath(name)));
      chart.initial?.range?.forEach((name) => dependencies.add(scopePath(name)));
      break;
    }
    case "raw_plotly_chart": {
      const chart = template as IRawPlotlyChart;
      if (chart.dataName) {
        dependencies.add(scopePath(chart.dataName));
      }
      if (chart.layoutName) {
        dependencies.add(scopePath(chart.layoutName));
      }
      collectActionSources(chart.data, scopePath, dependencies);
      collectActionSources(chart.layout, scopePath, dependencies);
      break;
    }
    case "3d_surface": {
      const chart = template as I3dSurface;
      chart.figures.forEach((figure) => {
        if (is3dStructureFigure(figure)) {
          dependencies.add(scopePath(figure.name));
        } else {
          dependencies.add(scopePath(figure.x_data.name));
          dependencies.add(scopePath(figure.y_data.name));
          dependencies.add(scopePath(figure.z_data.name));
        }
      });
      chart.range?.forEach((name) => dependencies.add(scopePath(name)));
      break;
    }
    default: {
      if (inputTypes.has(template.type) && template.name) {
        dependencies.add(scopePath(template.name));
      }
      break;
    }
  }

  return [...dependencies];
};

const pushDependency = (map: Record<IName, Array<IName>>, name: IName, dependencies: Array<IName>) => {
  if (!name || !dependencies.length) {
    return;
  }

  map[name] = uniq([...(map[name] || []), ...dependencies.filter((dependency) => dependency && dependency !== name)]);
};

export const areRelatedPaths = (left: IName, right: IName) =>
  left === right || left.startsWith(`${right}.`) || right.startsWith(`${left}.`);

export const buildDependencyIndex = (template?: ITemplate, model?: Record<string, unknown>): IDependencyIndex => {
  const directDependencies: Record<IName, Array<IName>> = {};
  const apiBackedFields = new Set<IName>();

  if (!template || !model) {
    return {
      apiBackedFields: [],
      directDependencies,
      transitiveApiDependencies: {},
    };
  }

  const visit = (node: IBase | Array<IBase>, scopePath: ScopePath = identityPath) => {
    if (isArray(node)) {
      node.forEach((item) => visit(item, scopePath));
      return;
    }

    if (node.type === "list") {
      const list = node as IList;
      const items = get(model, scopePath(list.name), []) as Array<unknown>;
      items.forEach((_item, index) => {
        const listScope: ScopePath = (name) => scopePath(buildPath(list.name, index, name));
        list.items.forEach((child) => visit(child, listScope));
      });
      return;
    }

    if (layoutTypes.has(node.type)) {
      const layout = node as { items?: Array<IBase> };
      layout.items?.forEach((child) => visit(child, scopePath));
      return;
    }

    if (inputTypes.has(node.type)) {
      const input = node as IInput;
      const scopedName = scopePath(input.name);
      pushDependency(directDependencies, scopedName, collectFieldDependencies(input, scopePath));
      if (hasQuery(input) && input.query) {
        apiBackedFields.add(scopedName);
      }
    }
  };

  visit(template.inputs);
  visit(template.outputs);

  const keys = uniq([...Object.keys(directDependencies), ...apiBackedFields]);
  const memo: Record<IName, Array<IName>> = {};

  const resolveForKey = (key: IName, visiting = new Set<IName>()): Array<IName> => {
    if (memo[key]) {
      return memo[key];
    }

    if (visiting.has(key)) {
      return [];
    }

    visiting.add(key);
    const resolved = new Set<IName>();

    if (apiBackedFields.has(key)) {
      resolved.add(key);
    }

    (directDependencies[key] || []).forEach((dependency) => {
      if (apiBackedFields.has(dependency)) {
        resolved.add(dependency);
      }

      (directDependencies[dependency] ? [dependency] : keys.filter((candidate) => areRelatedPaths(candidate, dependency)))
        .forEach((candidate) => resolveForKey(candidate, visiting).forEach((apiField) => resolved.add(apiField)));
    });

    visiting.delete(key);
    memo[key] = [...resolved];
    return memo[key];
  };

  keys.forEach((key) => resolveForKey(key));

  return {
    apiBackedFields: [...apiBackedFields],
    directDependencies,
    transitiveApiDependencies: memo,
  };
};

const expandToApiDependencies = (source: IName, dependencyIndex: IDependencyIndex): Array<IName> => {
  const expanded = new Set<IName>();
  const { apiBackedFields, transitiveApiDependencies } = dependencyIndex;

  apiBackedFields
    .filter((candidate) => areRelatedPaths(candidate, source))
    .forEach((candidate) => expanded.add(candidate));

  Object.entries(transitiveApiDependencies).forEach(([candidate, dependencies]) => {
    if (areRelatedPaths(candidate, source)) {
      dependencies.forEach((dependency) => expanded.add(dependency));
    }
  });

  return [...expanded];
};

export const getTemplateLoadingDependencies = (
  template: IBase,
  metadata?: IMetadata,
  scopePath: ScopePath = identityPath
): Array<IName> => {
  if (!metadata?.loading?.length) {
    return [];
  }

  let directDependencies = collectTemplateDependencies(template, scopePath);
  if (inputTypes.has(template.type) && template.name) {
    const input = template as IInput;
    const scopedName = scopePath(template.name);
    if (metadata.overridden?.includes(scopedName) && !(hasQuery(input) && input.query)) {
      directDependencies = directDependencies.filter((dependency) => dependency !== scopedName);
    }
  }
  if (!directDependencies.length) {
    return [];
  }

  const directLoadingDependencies = directDependencies.filter((dependency) =>
    metadata.loading.some((loadingPath) => areRelatedPaths(dependency, loadingPath))
  );

  const dependencyIndex = metadata.dependencyIndex || {
    apiBackedFields: [],
    directDependencies: {},
    transitiveApiDependencies: {},
  };
  const apiDependencies = uniq(
    directDependencies.flatMap((dependency) => expandToApiDependencies(dependency, dependencyIndex))
  );

  const transitiveLoadingDependencies = apiDependencies.filter((dependency) =>
    metadata.loading.some((loadingPath) => areRelatedPaths(dependency, loadingPath))
  );

  const resolvedLoadingDependencies = uniq([...directLoadingDependencies, ...transitiveLoadingDependencies]);

  return resolvedLoadingDependencies;
};

export const isTemplateRefreshing = (
  template: IBase,
  metadata?: IMetadata,
  scopePath: ScopePath = identityPath
) => getTemplateLoadingDependencies(template, metadata, scopePath).length > 0;
